import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import useAuthToken from "../../hooks/useAuthToken";
import AppConstants from "../../AppConstants";
import Swal from "sweetalert2";
import "./stripeStyles.scss";

// Dedicated axios instance for payment-method calls
const paymentAxios = axios.create();

export type AddPaymentMethodFormProps = {
  onAdded?: (paymentMethodId?: string) => void;
  onError?: (error: string) => void; // notify parent when there's an error
  onTokenExpired?: () => void; // notify parent when token is expired
  inline?: boolean; // if true, render without a <form> wrapper and use a non-submit button
  compact?: boolean; // if true, keep the component minimal (only used for useEffect optimization)
  existingMethods?: SavedMethod[]; // pass existing methods from parent to avoid duplicate API calls
};

type SavedMethod = {
  id: string;
  brand?: string;
  last4?: string;
  exp_month?: number;
  exp_year?: number;
  isDefault?: boolean;
};

function normalizeSavedMethods(input: any[]): SavedMethod[] {
  if (!Array.isArray(input)) return [];
  return input.map((m: any) => {
    const card = m?.card || m?.cardDetails || m?.card_info || m?.details || {};
    const brand = m?.brand || card?.brand;
    const last4 = m?.last4 || card?.last4;
    const expMonthRaw =
      m?.exp_month ?? m?.expMonth ?? card?.exp_month ?? card?.expMonth;
    const expYearRaw =
      m?.exp_year ?? m?.expYear ?? card?.exp_year ?? card?.expYear;
    const exp_month =
      typeof expMonthRaw === "string" ? parseInt(expMonthRaw, 10) : expMonthRaw;
    const exp_year =
      typeof expYearRaw === "string" ? parseInt(expYearRaw, 10) : expYearRaw;
    return {
      id:
        m?.stripePaymentMethodId ||
        m?.id ||
        m?.paymentMethodId ||
        m?.payment_method_id ||
        "",
      brand,
      last4,
      exp_month,
      exp_year,
      isDefault:
        m?.isDefault || m?.default || m?.is_primary || m?.primary || false
    } as SavedMethod;
  });
}

function isTokenExpiredError(error: any): boolean {
  return error?.response?.status === 401 || error?.response?.status === 403;
}

export function AddPaymentMethodForm({
  onAdded,
  onError,
  onTokenExpired,
  inline = false,
  compact = false,
  existingMethods = []
}: AddPaymentMethodFormProps = {}) {
  const stripe = useStripe();
  const elements = useElements();
  const { userToken, ready } = useAuthToken();

  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [methods, setMethods] = useState<SavedMethod[]>(existingMethods);
  const [loadingMethods, setLoadingMethods] = useState(false);
  const [tokenExpired, setTokenExpired] = useState(false);

  const disabled = useMemo(
    () =>
      !ready ||
      !stripe ||
      !elements ||
      !userToken ||
      submitting ||
      tokenExpired,
    [ready, stripe, elements, userToken, submitting, tokenExpired]
  );

  const onSubmit = useCallback(
    async (e?: React.FormEvent | React.MouseEvent) => {
      if (e && typeof (e as any).preventDefault === "function")
        (e as any).preventDefault();
      if (e && typeof (e as any).stopPropagation === "function")
        (e as any).stopPropagation();
      setMessage(null);
      setError(null);

      if (!stripe || !elements) return;
      if (!userToken) {
        setError("Missing token. Open this page via the app link.");
        return;
      }

      const card = elements.getElement(CardElement);
      if (!card) {
        setError("Card element is not ready.");
        return;
      }

      try {
        setSubmitting(true);

        // Tokenize to read brand/last4/exp for duplicate check
        const tokenRes = await stripe.createToken(card);
        if (tokenRes.error) {
          setError(tokenRes.error.message || "Card validation failed.");
          return;
        }
        const tCard = tokenRes.token?.card;
        const newBrand = (tCard?.brand || "").toUpperCase();
        const newLast4 = tCard?.last4 || "";
        const newExpMonth = tCard?.exp_month;
        const newExpYear = tCard?.exp_year;
        const isDuplicate = methods.some((m) => {
          const mBrand = (m.brand || "").toUpperCase();
          const brandMatch = !!newBrand && !!mBrand && newBrand === mBrand;
          const last4Match = !!newLast4 && !!m.last4 && newLast4 === m.last4;
          const expMonthMatch =
            m.exp_month && newExpMonth ? m.exp_month === newExpMonth : true;
          const expYearMatch =
            m.exp_year && newExpYear ? m.exp_year === newExpYear : true;
          return brandMatch && last4Match && expMonthMatch && expYearMatch;
        });
        if (isDuplicate) {
          setError("This card already exists in your saved methods.");
          return;
        }

        // 1) Create SetupIntent
        const setupIntentRes = await paymentAxios.post(
          AppConstants.PAYMENTS_SETUP_INTENT_API,
          {},
          { headers: { Authorization: `Bearer ${userToken}` } }
        );

        const clientSecret: string | undefined =
          setupIntentRes.data?.data?.clientSecret;
        if (!clientSecret) {
          setError("Failed to initialize card setup.");
          return;
        }

        // 2) Confirm card setup
        const confirmRes = await stripe.confirmCardSetup(clientSecret, {
          payment_method: { card },
          // Ensure redirect-based 3DS returns to our page
          return_url: `${window.location.origin}/stripe/return?flow=pm`
        });

        if (confirmRes.error) {
          setError(confirmRes.error.message || "Failed to confirm card setup.");
          return;
        }

        const setupPaymentMethod = confirmRes.setupIntent?.payment_method as
          | string
          | { id?: string }
          | undefined;
        const paymentMethodId =
          typeof setupPaymentMethod === "string"
            ? setupPaymentMethod
            : setupPaymentMethod?.id;
        const setupIntentId = confirmRes.setupIntent?.id;
        if (!paymentMethodId) {
          setError("No payment method returned from setup.");
          return;
        }

        // 3) Attach/save the payment method
        const res = await paymentAxios.post(
          AppConstants.PAYMENTS_ADD_PAYMENT_METHOD_API,
          { paymentMethodId, setupIntentId },
          { headers: { Authorization: `Bearer ${userToken}` } }
        );

        if (res.status >= 200 && res.status < 300) {
          // Show success toast
          Swal.fire({
            icon: "success",
            title: "Success!",
            text: "Payment method added successfully",
            timer: 2000,
            showConfirmButton: false,
            toast: true,
            position: "top-end"
          });

          setMessage("Payment method added successfully.");
          
          // Make the newly added payment method the default/primary
          try {
            await paymentAxios.put(
              AppConstants.PAYMENTS_SET_DEFAULT_METHOD_API,
              { paymentMethodId },
              { headers: { Authorization: `Bearer ${userToken}` } }
            );
          } catch {}
          
          // Clear the form
          const card = elements.getElement(CardElement);
          if (card) {
            card.clear();
          }
          
          try {
            onAdded?.(paymentMethodId);
          } catch {}
          
          return;
        } else {
          const errorMessage = "Failed to save the payment method.";
          setError(errorMessage);
          
          // Show error toast
          Swal.fire({
            icon: "error",
            title: "Error",
            text: errorMessage,
            confirmButtonColor: "#28a745",
            toast: true,
            position: "top-end",
            timer: 3000,
            showConfirmButton: true
          });
          
          try {
            onError?.(errorMessage);
          } catch {}
        }
      } catch (err: any) {
        if (isTokenExpiredError(err)) {
          setTokenExpired(true);
          try {
            onTokenExpired?.();
          } catch {}
          setError(null);
        } else {
          const apiMessage = err?.response?.data?.message || err?.message;
          const errorMessage = apiMessage || "Unexpected error.";
          setError(errorMessage);

          // Show error toast
          Swal.fire({
            icon: "error",
            title: "Error",
            text: errorMessage,
            confirmButtonColor: "#28a745",
            toast: true,
            position: "top-end",
            timer: 3000,
            showConfirmButton: true
          });

          try {
            onError?.(errorMessage);
          } catch {}
        }
      } finally {
        setSubmitting(false);
      }
    },
    [stripe, elements, userToken, methods, onTokenExpired]
  );

  // Load saved methods for duplicate checks only (not for display)
  useEffect(() => {
    // If methods are provided from parent, use them
    if (existingMethods.length > 0) {
      setMethods(existingMethods);
      return;
    }

    const load = async () => {
      if (!ready || !userToken) return;
      setLoadingMethods(true);
      try {
        const res = await paymentAxios.get(
          AppConstants.PAYMENTS_LIST_METHODS_API,
          {
            headers: { Authorization: `Bearer ${userToken}` }
          }
        );
        const payload = res.data;
        let rawList: any[] = [];
        if (Array.isArray(payload)) rawList = payload as any[];
        else if (Array.isArray(payload?.data?.paymentMethods))
          rawList = payload.data.paymentMethods;
        else if (Array.isArray(payload?.data)) rawList = payload.data;
        else if (Array.isArray(payload?.paymentMethods))
          rawList = payload.paymentMethods;
        else if (Array.isArray(payload?.methods)) rawList = payload.methods;
        const list = normalizeSavedMethods(rawList);
        setMethods(list);
      } catch (err: any) {
        if (isTokenExpiredError(err)) {
          setTokenExpired(true);
          try {
            onTokenExpired?.();
          } catch {}
        }
      } finally {
        setLoadingMethods(false);
      }
    };
    load();
  }, [ready, userToken, onTokenExpired, existingMethods]);

  const content = (
    <>
      <div
        style={{
          border: "1px solid #E7ECF1",
          borderRadius: 12,
          padding: 16,
          background: "#fff",
          transition: "border-color 0.3s ease, box-shadow 0.3s ease"
        }}
        onFocusCapture={(e) => {
          e.currentTarget.style.borderColor = "#08A45D";
          e.currentTarget.style.boxShadow = "0 0 0 3px rgba(8, 164, 93, 0.1)";
        }}
        onBlurCapture={(e) => {
          e.currentTarget.style.borderColor = "#E7ECF1";
          e.currentTarget.style.boxShadow = "none";
        }}
      >
        <CardElement
          options={{
            hidePostalCode: true,
            style: {
              base: {
                fontSize: "16px",
                color: "#1f2937",
                fontFamily: "Gilroy-Regular, sans-serif",
                "::placeholder": { color: "#9ca3af" }
              },
              invalid: {
                color: "#dc3545"
              }
            }
          }}
        />
      </div>
      {inline ? (
        <button
          type="button"
          onClick={onSubmit}
          disabled={disabled}
          className="btn btn-primary w-100 mt-3 py-2 text-white fw-semibold"
        >
          {submitting ? "Saving…" : "Save Payment Method"}
        </button>
      ) : (
        <button
          type="submit"
          disabled={disabled}
          className="btn btn-primary w-100 mt-3 py-2 text-white fw-semibold"
        >
          {submitting ? "Saving…" : "Save Payment Method"}
        </button>
      )}

      {message && <p style={{ color: "green", marginTop: 12 }}>{message}</p>}
      {error && <p style={{ color: "crimson", marginTop: 12 }}>{error}</p>}
    </>
  );

  if (inline) {
    // Avoid nested <form> when embedded inside another form
    return <div>{content}</div>;
  }

  return <form onSubmit={onSubmit}>{content}</form>;
}

export default AddPaymentMethodForm;
